/**
 * useLoading — owns GET /api/v1/loading/vs-capacity, the forward Loading vs Capacity
 * table. TanStack Query + the carried AuthContext token (CD-7: hooks fetch). A 422
 * (unbuilt load mart, unserved entity, unknown basis) arrives as the ApiError the surface
 * renders verbatim — offline the mock serves no load frame, so this refuses honestly
 * rather than drawing an empty grid as if it were truth.
 *
 * The basis parameter is the SAME governed toggle as /utilisation/availability, so the
 * load table and the utilisation tiles always agree on what a machine-hour of capacity is.
 */

import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import type { LoadDrill, LoadMode, LoadVsCapacityView } from '../types/loading'
import type { WorksOrderRouteView } from '../types/worksOrder'
import { ApiError, fetchApi } from './apiFetch'

/** `basis` is OMITTED when null, so the server applies the governed headline from
 *  registry/capacity_basis.yaml. Sending a client-chosen default would put the denominator
 *  back in the frontend under a different name. */
const path = (entity: string, weeks: number, basis: string | null, mode: LoadMode): string => {
  const params = new URLSearchParams({ entity, weeks: String(weeks), mode })
  if (basis !== null) params.set('basis', basis)
  return `loading/vs-capacity?${params.toString()}`
}

/**
 * Retry the transport, never the answer.
 *
 * A typed 4xx on this API is a DECISION — "no scheduled load for entity 'oracle'", "no
 * levelled load for MATSURA @ 2026-10-19". Retrying it asks the same question three times
 * and gets the same answer three times, and the cost is not just latency: while a query is
 * retrying, TanStack reports `pending` with a null error, and it PAUSES retries entirely
 * when the browser believes it is offline. A refusal then never reaches the surface at all
 * and the panel renders a silent gap where a sentence belongs. Server faults are the only
 * thing worth asking twice.
 */
export const retryTransportOnly = (count: number, error: ApiError): boolean =>
  error.status >= 500 && count < 2

/** One entity's answer, whichever way it went. */
export interface LoadingEntity {
  entity: string
  view?: LoadVsCapacityView
  error?: ApiError
  isLoading: boolean
}

/**
 * Several entities at once — what the Group scope needs.
 *
 * A REFUSAL IS A RESULT HERE, not a failure. Both sites carry a scheduled-load model since
 * 19 Aug 2026 (Oracle joined the mart when Red Ant's routing and shift calendar were
 * ingested), but an entity can still refuse — an unbuilt mart, a window with nothing in it,
 * a book whose every hour is withheld. Each one comes back typed, and the surface says which
 * site is missing and why. Dropping the entity silently would let a Group total quietly mean
 * one site, which is the sort of thing nobody notices until they price a job on it.
 * See retryTransportOnly: a typed refusal is an answer, and answers are not retried.
 */
export function useLoadingAll(
  entities: readonly string[],
  weeks: number,
  basis: string | null,
  mode: LoadMode
): LoadingEntity[] {
  const { token } = useAuth()
  const results = useQueries({
    queries: entities.map((entity) => ({
      queryKey: ['loading-vs-capacity', entity, weeks, basis, mode],
      queryFn: () => fetchApi<LoadVsCapacityView>(path(entity, weeks, basis, mode), token),
      enabled: token !== null,
      retry: retryTransportOnly,
    })),
  })
  return results.map((r, i) => ({
    entity: entities[i],
    view: r.data as LoadVsCapacityView | undefined,
    error: (r.error ?? undefined) as ApiError | undefined,
    isLoading: r.isLoading,
  }))
}

/**
 * `mode` picks which reading of the same hours the backend serves. It is a query
 * parameter rather than a client-side derivation on purpose: levelling needs the capacity
 * denominator, and that is resolved server-side against the governed basis. Deriving it
 * here would fork the definition — the exact failure G25 exists to prevent.
 */
export function useLoading(
  entity: string,
  weeks: number,
  basis: string,
  mode: LoadMode = 'as_scheduled'
): UseQueryResult<LoadVsCapacityView, ApiError> {
  const { token } = useAuth()
  const params = new URLSearchParams({ entity, weeks: String(weeks), basis, mode })
  return useQuery<LoadVsCapacityView, ApiError>({
    queryKey: ['loading-vs-capacity', entity, weeks, basis, mode],
    queryFn: () => fetchApi<LoadVsCapacityView>(`loading/vs-capacity?${params.toString()}`, token),
    enabled: token !== null,
    retry: retryTransportOnly,
  })
}

/**
 * The operations behind ONE Loading vs Capacity cell. `includeOverdue` must mirror the
 * cell's own includes_overdue flag — the folded first column absorbs every pre-window week,
 * so its drill has to widen to match or the sum_check would go red on a correct cell.
 *
 * `mode` must match the grid the cell was clicked on, and carries `weeks`/`basis` with it:
 * a levelled bucket holds work scheduled in earlier weeks, so it cannot be resolved from
 * its own week alone — the spill-forward walk depends on the whole window and on the
 * capacity it is spilling against. `weeks` is therefore the window the CELL was drawn in,
 * never a default: a drill on a different horizon than its cell would not reconcile.
 */
export function useLoadingDrill(
  entity: string,
  resourceGroup: string | null,
  weekStart: string | null,
  includeOverdue: boolean,
  mode: LoadMode = 'as_scheduled',
  // NO DEFAULT since 31 Aug 2026, for the same reason `basis` has none below: the horizon is
  // a choice now, and a 13 sitting here would answer a question about a window the reader is
  // not looking at. The caller passes the SERVED week count.
  weeks: number,
  // NO DEFAULT (20 Aug 2026). `basis = 'calendar_24h'` sat here as the last copy of a basis
  // literal in React — dead, because WeekPanel always passes the served basis, and dead is
  // how the first one survived long enough to become the governance problem G15 names. The
  // basis has ONE home: registry/capacity_basis.yaml, echoed by the API.
  basis: string
): UseQueryResult<LoadDrill, ApiError> {
  const { token } = useAuth()
  const params = new URLSearchParams({
    entity,
    resource_group: resourceGroup ?? '',
    week_start: weekStart ?? '',
    include_overdue: String(includeOverdue),
    mode,
    weeks: String(weeks),
    basis,
  })
  return useQuery<LoadDrill, ApiError>({
    queryKey: ['loading-drill', entity, resourceGroup, weekStart, includeOverdue, mode, weeks, basis],
    queryFn: () => fetchApi<LoadDrill>(`loading/drill?${params.toString()}`, token),
    enabled: token !== null && resourceGroup !== null && weekStart !== null,
    retry: retryTransportOnly,
  })
}

/**
 * ONE works order, whole: every operation, the machine it runs on, and when.
 *
 * Deliberately NOT windowed and NOT scoped to a resource group. The reader arrives from one
 * cell of one machine's week, and the question they are now asking — "where does this job
 * go" — is the one question the whole capacity surface is organised against answering. The
 * `weeks` parameter is passed only so the backend can label which legs fall outside the
 * window ON SCREEN; it never clips them, and it moves with the chosen horizon.
 */
export function useWorksOrder(
  entity: string,
  woKey: string | null,
  weeks: number
): UseQueryResult<WorksOrderRouteView, ApiError> {
  const { token } = useAuth()
  const params = new URLSearchParams({ entity, wo_key: woKey ?? '', weeks: String(weeks) })
  return useQuery<WorksOrderRouteView, ApiError>({
    queryKey: ['loading-works-order', entity, woKey, weeks],
    queryFn: () => fetchApi<WorksOrderRouteView>(`loading/works-order?${params.toString()}`, token),
    enabled: token !== null && woKey !== null,
    retry: retryTransportOnly,
  })
}
